import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState, Skeleton, EmptyState } from '@vta/ui';
import { apiClient, ApiError } from '../lib/api-client.ts';
import { OgMeta } from '../lib/og-meta.tsx';
import {
  DivisionTeamsTable,
  type DivisionTeamRow,
} from '../features/divisions/DivisionTeamsTable.tsx';

interface DivisionResponse {
  divisionSlug: string;
  divisionName: string;
  teams: DivisionTeamRow[];
}

export const Route = createFileRoute('/division/$divisionSlug')({
  loader: async ({ params }) => {
    try {
      return await apiClient.get<DivisionResponse>(`/api/divisions/${encodeURIComponent(params.divisionSlug)}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  component: DivisionRoute,
});

function DivisionRoute() {
  const { divisionSlug } = Route.useParams();
  const initial = Route.useLoaderData() as DivisionResponse | null;

  const query = useQuery<DivisionResponse | null>({
    queryKey: ['division', divisionSlug],
    initialData: initial,
    queryFn: ({ signal }) =>
      apiClient
        .get<DivisionResponse>(`/api/divisions/${encodeURIComponent(divisionSlug)}`, { signal })
        .catch((err) => {
          if (err instanceof ApiError && err.status === 404) return null;
          throw err;
        }),
  });

  if (query.isLoading) return <DivisionSkeleton />;
  if (query.isError) {
    return (
      <main className="container mx-auto py-10">
        <ErrorState title="Could not load division" retry={() => query.refetch()} />
      </main>
    );
  }

  const data = query.data;
  if (!data) {
    return (
      <main className="container mx-auto py-10">
        <OgMeta
          title="Division not found"
          description="No teams catalogued for this division."
          pathname={`/division/${divisionSlug}`}
        />
        <EmptyState
          title="Division not found"
          description="Division metadata may not be synced yet — coverage notes are on the About page."
        />
      </main>
    );
  }

  return (
    <>
      <OgMeta
        title={data.divisionName}
        description={`${data.teams.length} teams competing in ${data.divisionName}.`}
        pathname={`/division/${data.divisionSlug}`}
      />
      <main className="container mx-auto flex flex-col gap-8 py-8 md:py-12">
        <header className="flex flex-col gap-2 border-b border-border-subtle pb-6">
          <p className="text-2xs uppercase tracking-[0.3em] text-fg-subtle">Division</p>
          <h1 className="font-display text-3xl text-fg sm:text-4xl">{data.divisionName}</h1>
          <p className="text-sm text-fg-muted">
            {data.teams.length} team{data.teams.length === 1 ? '' : 's'} in this division.
          </p>
        </header>

        <DivisionTeamsTable teams={data.teams} />
      </main>
    </>
  );
}

function DivisionSkeleton() {
  return (
    <main className="container mx-auto flex flex-col gap-6 py-10">
      <Skeleton className="h-12 w-1/2" />
      <Skeleton className="h-72 w-full" />
    </main>
  );
}
